import React from "react";
import Stack from "@mui/material/Stack";
import theme from "Theme/AppTheme";
import AcxButton from "../../../AcxButton";
import PlayControl from "components/AudioPlayer/PlayControl";
import { PlayStatus } from "../../../Audio/MicroPlayerControl";
import { PlayerOptions } from "components/SoundClipEditor/SoundClipEditor";
import EddyChip from "../../../Chat/Views/EddyChip";
import { useStore } from "utils/useStore";
import { AcxTranscriptionStore } from "../../Stores/AcxTranscriptionStore";
import { observer } from "mobx-react";
import { BlurbBoxSpeakerTitle } from "./BlurbBoxSpeakerTitle";
import { Blurb } from "./Blurb";
import { Grid } from "@mui/material";
import { SpeakerStore } from "../../Stores/SpeakerStore/SpeakerStore";
import ScissorsSvg from "SvgIcons/ScissorsSvg";
import { BlurbText } from "./BlurbText";
import AcxChip from "components/UI/AcxChip";
import { sxClsx } from "utils/styleClsx";

interface BlurbBoxProps {
    blurb: Blurb;
    index: number;
    playerBlurb?: (playerOptions: PlayerOptions) => void;
    onCreateClip: (timeInterval: number[]) => Promise<void>;
    cancelHighlight?: () => void;
    audioClip?: { status: "default" | "success" | "error" | "loading" };
    toolTipOpen?: boolean;
    hasEvaluation: boolean;
    speakerStore?: SpeakerStore;
    disableClip?: boolean;
}

const BlurbBox: React.FC<BlurbBoxProps> = observer((props) => {
    const {
        blurb,
        playerBlurb,
        onCreateClip,
        audioClip,
        hasEvaluation,
        speakerStore,
        disableClip,
    } = props;
    const store = useStore(AcxTranscriptionStore);

    const [selectedPlayingBlurb, setSelectedPlayingBlurb] = React.useState({
        id: "0",
        isPlaying: false,
    });

    const playAudio = () => {
        playerBlurb?.({
            id: blurb.timeInterval.join("-"),
            isPlaying: true,
            startTime: blurb.timeInterval[0],
            endTime: blurb.timeInterval[1],
        });
        setSelectedPlayingBlurb({
            id: blurb.timeInterval.join("-"),
            isPlaying: true,
        });
    };

    const pauseAudio = () => {
        playerBlurb?.({
            id: blurb.timeInterval.join("-"),
            isPlaying: false,
            startTime: blurb.timeInterval[0],
            endTime: blurb.timeInterval[1],
        });
        setSelectedPlayingBlurb({
            id: blurb.timeInterval.join("-"),
            isPlaying: false,
        });
    };

    const [hovered, setHovered] = React.useState(false);

    return (
        <Grid
            onMouseEnter={() => {
                setHovered(true);
            }}
            onMouseLeave={() => {
                setHovered(false);
            }}
            container
            sx={{
                py: 1,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                px: 1,
                position: "relative",
                "&:hover": {
                    backgroundColor: "#F9FAFB",
                    "& $hiddenActions": {
                        visibility: "visible",
                    },
                },
            }}
            onMouseDown={React.useCallback(
                (e) => {
                    e.stopPropagation();
                    const startTimestamp = blurb.timeInterval[0];
                    const endTimestamp = blurb.timeInterval[1];
                    store.setSelectedStartTimestamp(startTimestamp);
                    store.setSelectedEndTimestamp(null);
                    store.setSelectedEndTimestampTemp(endTimestamp);
                },
                [store, blurb.timeInterval],
            )}
            onMouseUp={React.useCallback(
                (e) => {
                    if (
                        !blurb.timeInterval ||
                        (!store.selectedStartTimestamp &&
                            store.selectedStartTimestamp !== 0)
                    )
                        return;
                    e.stopPropagation();
                    const thisBlubStartTimestamp = blurb.timeInterval[0];
                    const endTimestamp = blurb.timeInterval[1];

                    if (
                        thisBlubStartTimestamp ===
                            store.selectedStartTimestamp &&
                        store.selectingViaAudio
                    ) {
                        // Selected single blurb AND we WERE selecting via audio, so they just clicked away the existing highlight
                        store.setSelectedStartTimestamp(null);
                        store.setSelectedEndTimestamp(null);
                        store.setSelectedEndTimestampTemp(null);
                        store.setSelectingViaAudio(false);
                        return;
                    }

                    store.setSelectingViaAudio(false);

                    store.setSelectedEndTimestamp(endTimestamp);
                    // If end timestamp is less than start timestamp, use the temp end and this start
                    if (endTimestamp < store.selectedStartTimestamp) {
                        store.setSelectedStartTimestamp(thisBlubStartTimestamp);
                        store.setSelectedEndTimestamp(
                            store.selectedEndTimestampTemp,
                        );
                    }
                },
                [store, blurb.timeInterval],
            )}
        >
            <Grid
                sx={{ display: "flex", spacing: 1 }}
                xs={10}
                xl={11}
                item
                flexDirection={"row"}
            >
                <Grid item sx={{ display: "flex", alignItems: "center" }}>
                    <BlurbBoxSpeakerTitle
                        speakerStore={speakerStore}
                        blurb={blurb}
                        isDisabled={disableClip}
                    />
                </Grid>
                <Grid
                    item
                    sx={sxClsx(
                        {
                            paddingRight: "90px",
                            paddingLeft: theme.spacing(1),
                        },
                        blurb.foundAdverse && {
                            borderLeft: `2px solid ${theme.palette.highlight.adverseEvent.main}`,
                        },
                        blurb.foundEddy && {
                            borderLeft: `2px solid ${theme.palette.highlight.eddy.main}`,
                        },
                    )}
                >
                    <Stack direction={"row"}>
                        <div style={{ height: "100%", minWidth: "141px" }}>
                            <Grid>
                                <BlurbText blurb={blurb} store={store} />
                            </Grid>
                            <Grid
                                sx={{
                                    WebkitTouchCallout: "none",
                                    WebkitUserSelect: "none",
                                    KhtmlUserSelect: "none",
                                    MozUserSelect: "none",
                                    MsUserSelect: "none",
                                    UserSelect: "none",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                {blurb.foundEddy && (
                                    <EddyChip altText="Eddy Effect Signal" />
                                )}
                                {blurb.foundAdverse && (
                                    <AcxChip
                                        color="adverse"
                                        size="small"
                                        label="Safety Event"
                                    />
                                )}
                            </Grid>
                        </div>
                    </Stack>
                </Grid>
            </Grid>
            {!disableClip && hovered && (
                <Grid
                    xs={2}
                    xl={1}
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                    }}
                    item
                    className="highlightIgnore"
                    onMouseUp={(e) => {
                        e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <PlayControl
                        svgSize="small"
                        buttonStyle={{
                            minWidth: "40px",
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            justifyContent: "center",
                            border: "solid 1px #E4E4E7",
                            backgroundColor: "transparent",
                            margin: 0,
                            borderRadius: theme.shape.borderRadius,
                        }}
                        play={() => playAudio()}
                        pause={() => pauseAudio()}
                        status={
                            selectedPlayingBlurb.id ===
                                blurb.timeInterval.join("-") &&
                            selectedPlayingBlurb.isPlaying
                                ? PlayStatus.Play
                                : PlayStatus.Paused
                        }
                        color={"default"}
                    />
                    {hasEvaluation && !disableClip && (
                        <AcxButton
                            fullWidth
                            size="small"
                            style={{
                                minWidth: "40px",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                justifyContent: "center",
                                border: "solid 1px #E4E4E7",
                                backgroundColor: "transparent",
                                margin: 0,
                            }}
                            tooltip="Create a clip"
                            loading={audioClip?.status === "loading"}
                            onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                await onCreateClip(blurb.timeInterval);
                            }}
                        >
                            <ScissorsSvg />
                        </AcxButton>
                    )}
                </Grid>
            )}
        </Grid>
    );
});

export default BlurbBox;
